import { useState } from 'react';
import { ShoppingCart, Check, Copy, Store, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { GROCERY_SERVICES } from './constants';

const getUnchecked = (list) => (list ? list.items.filter((i) => !i.checked) : []);

const formatAsText = (items) =>
  items.map((item) => `${item.quantity} ${item.unit} ${item.name}`).join('\n');

export const ShoppingListDialog = ({ open, onOpenChange, shoppingList, onToggleItem }) => {
  const [showOrderOptions, setShowOrderOptions] = useState(false);

  const unchecked = getUnchecked(shoppingList);

  const copyListToClipboard = () => {
    if (unchecked.length === 0) {
      toast.error('Tous les articles sont cochés !');
      return;
    }
    navigator.clipboard
      .writeText(formatAsText(unchecked))
      .then(() => toast.success('Liste copiée dans le presse-papier !'))
      .catch(() => toast.error('Impossible de copier la liste'));
  };

  const orderFromService = (service) => {
    if (unchecked.length === 0) {
      toast.error('Tous les articles sont cochés !');
      return;
    }
    const query = unchecked.slice(0, 5).map((i) => i.name).join(' ');
    window.open(service.searchUrl(query), '_blank');
    toast.success(`Ouverture de ${service.name}...`);
    setShowOrderOptions(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-worldplate-primary" />
              Liste de courses
            </DialogTitle>
          </DialogHeader>

          {shoppingList && (
            <>
              <p className="text-sm text-worldplate-muted mb-4">
                {shoppingList.recipe_count} recettes • {shoppingList.items.length} ingrédients
              </p>

              <div className="overflow-y-auto flex-1 space-y-2 pr-2 max-h-[40vh]">
                {shoppingList.items.length === 0 ? (
                  <div className="text-center py-8 text-worldplate-muted">
                    <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Aucun ingrédient à acheter</p>
                  </div>
                ) : (
                  shoppingList.items.map((item, index) => (
                    <button
                      key={`${item.name}-${item.unit}`}
                      onClick={() => onToggleItem(index)}
                      className={`w-full p-3 text-left rounded-lg transition-colors flex items-center gap-3 ${
                        item.checked
                          ? 'bg-worldplate-secondary/10 line-through text-worldplate-muted'
                          : 'bg-worldplate-subtle hover:bg-worldplate-primary/10'
                      }`}
                      data-testid={`shopping-item-${index}`}
                    >
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          item.checked
                            ? 'bg-worldplate-secondary border-worldplate-secondary'
                            : 'border-worldplate-muted'
                        }`}
                      >
                        {item.checked && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-1">
                        <span className="font-medium">{item.quantity} {item.unit}</span>
                        <span className="ml-2">{item.name}</span>
                      </div>
                    </button>
                  ))
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-worldplate-border space-y-4">
                <p className="text-sm text-worldplate-muted text-center">
                  {shoppingList.items.filter((i) => i.checked).length} / {shoppingList.items.length} articles cochés
                </p>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={copyListToClipboard}
                    className="flex-1 gap-2"
                    data-testid="copy-list-btn"
                  >
                    <Copy className="w-4 h-4" />
                    Copier la liste
                  </Button>
                  <Button
                    onClick={() => setShowOrderOptions(true)}
                    className="flex-1 gap-2 bg-worldplate-primary hover:bg-worldplate-primary/90"
                    data-testid="order-btn"
                  >
                    <Store className="w-4 h-4" />
                    Commander
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Order Options */}
      <Dialog open={showOrderOptions} onOpenChange={setShowOrderOptions}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Store className="w-5 h-5 text-worldplate-primary" />
              Commander les ingrédients
            </DialogTitle>
          </DialogHeader>

          <p className="text-sm text-worldplate-muted mb-4">
            Choisissez votre service de courses préféré. La liste des ingrédients sera pré-remplie pour faciliter vos achats.
          </p>

          <div className="space-y-3">
            {GROCERY_SERVICES.map((service) => (
              <button
                key={service.id}
                onClick={() => orderFromService(service)}
                className="w-full p-4 rounded-xl border border-worldplate-border hover:border-worldplate-primary hover:bg-worldplate-subtle transition-colors flex items-center gap-4 group"
                data-testid={`order-${service.id}`}
              >
                <div className={`w-12 h-12 ${service.color} rounded-xl flex items-center justify-center text-2xl`}>
                  {service.logo}
                </div>
                <div className="flex-1 text-left">
                  <p className="font-semibold text-worldplate-deep group-hover:text-worldplate-primary">
                    {service.name}
                  </p>
                  <p className="text-sm text-worldplate-muted">Ouvrir avec votre liste</p>
                </div>
                <ExternalLink className="w-5 h-5 text-worldplate-muted group-hover:text-worldplate-primary" />
              </button>
            ))}
          </div>

          <div className="mt-4 pt-4 border-t border-worldplate-border">
            <p className="text-xs text-worldplate-muted text-center">
              {unchecked.length} articles non cochés seront recherchés
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ShoppingListDialog;
